import Image from "next/image"

import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"

/**
 * "New: Ahead membership" announcement pill.
 *
 * Originated on the membership hero (`/membership`) and reused across the site
 * (homepage hero, homepage membership section). The icon's bg uses
 * `/images/membership/pill-icon-bg.png` for the soft gradient look from Figma.
 *
 * The component renders white text on a translucent dark pill, designed to sit
 * over a photo or dark background. The label is passed in (already
 * translated by the caller) so this component stays i18n-agnostic and
 * reusable by both server and client trees.
 */
/**
 * The Ahead membership mark on its own — the Figma gradient PNG crop with
 * the white Ahead arrow centered, in a circle. Extracted from
 * `NewMembershipPill` so other surfaces (e.g. the /go/bloodtest membership
 * comparison card) reuse the exact same logo instead of re-creating it.
 * `size` scales the whole mark (the bg crop offsets scale with it).
 */
export function MembershipPillIcon({
  size = 29,
  className,
}: {
  size?: number
  className?: string
}) {
  const scale = size / 29
  return (
    <span
      className={cn(
        "relative flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        className
      )}
      style={{ width: size, height: size }}
    >
      <Image
        src={blobMediaUrl("/images/membership/pill-icon-bg.png")}
        alt=""
        width={Math.round(144 * scale)}
        height={Math.round(81 * scale)}
        aria-hidden="true"
        className="pointer-events-none absolute max-w-none"
        style={{ left: `${-8.62 * scale}px`, top: `${-24.56 * scale}px` }}
      />
      <svg
        width={15 * scale}
        height={16 * scale}
        viewBox="0 0 15 16"
        fill="none"
        aria-hidden="true"
        className="relative"
      >
        <path
          d="M11.3742 0H0V1.99316L11.2938 1.71601C12.3158 1.69133 13.1522 2.51707 13.1311 3.53073L12.8861 15.7934H14.8593V3.4586C14.8593 1.54897 13.2976 0 11.3723 0H11.3742Z"
          fill="white"
        />
        <path
          d="M9.37226 4.30513L0 14.3525L1.42584 15.8028L10.8517 5.66047C11.2325 5.25045 11.2019 4.61264 10.7828 4.24059C10.3732 3.87612 9.74546 3.9046 9.37226 4.30513Z"
          fill="white"
        />
      </svg>
    </span>
  )
}

export function NewMembershipPill({
  label,
  className,
  noMagicStroke = false,
  variant = "dark",
}: {
  label: string
  className?: string
  /** Suppress the rotating-gradient hover stroke. Use when the pill is a
   *  label rather than an interactive affordance (e.g. the homepage
   *  membership section, where the real CTAs sit right below). */
  noMagicStroke?: boolean
  /** "dark" (default) — white text on a translucent dark pill, for photos /
   *  dark backgrounds. "light" — dark text on a faint neutral pill, for white
   *  backgrounds (e.g. the desktop membership hero). The gradient icon is
   *  unchanged (it reads on both). */
  variant?: "dark" | "light"
}) {
  const light = variant === "light"
  return (
    <div
      className={cn(
        "type-label-lg inline-flex items-center justify-center gap-2 rounded-full py-1 pr-3 pl-1 normal-case backdrop-blur-[5px]",
        light
          ? "bg-neutral-900/[0.06] text-neutral-900"
          : "bg-black/20 text-white",
        !noMagicStroke && "pill-magic-stroke",
        className
      )}
    >
      <MembershipPillIcon />
      {label}
    </div>
  )
}
